import { FormattedMessage } from 'react-intl';

import {
  CreatePostButton,
  getTrackingUrl,
} from '@xing-com/crate-entity-pages-common';
import { useEditStore } from '@xing-com/crate-entity-pages-entity-pages-shared-context-edit-context';
import { usePageContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-entity-page-context';
import {
  PROMOTE_URL,
  XBM_STATISTICS_URL,
} from '@xing-com/crate-entity-pages-entity-pages-shared-urls';
import { IconInfo, IconStatistics } from '@xing-com/icons';
import { ModalPage } from '@xing-com/modal-page';
import { usePopOver } from '@xing-com/pop-over';

import { useResizeObserver } from '../../hooks/use-resize-observer/use-resize-observer';
import { useStickyBarWithOffset } from '../../hooks/use-sticky-bar-with-offset/use-sticky-bar-with-offset';
import { mergeRefs } from '../../utils/helpers';
import { trackOpenNavInfoBox } from '../../utils/tracking';
import { EditBarBack } from '../edit-bar-back/edit-bar-back';
import { PageInfo } from '../page-info/page-info';
import { ToggleEditMode } from '../toggle-edit-mode/toggle-edit-mode';

import * as Styled from './edit-bar.styles';

type EditBarWebProps = {
  displayNewsButtons: boolean;
  displayBackButton: boolean;
};
export const EditBarWeb: React.FC<EditBarWebProps> = ({
  displayNewsButtons,
  displayBackButton,
}) => {
  const { pageContext, isSubpage } = usePageContext();
  const isEditing = useEditStore(({ isEditing }) => isEditing);

  const pageInfoModal = usePopOver();

  const [ref, entry] = useResizeObserver();
  const [stickyBarRef, stickyBarFixed] = useStickyBarWithOffset();

  return (
    <Styled.EditBar
      data-testid="edit-bar-desktop"
      $isSticky={!!(pageContext.isShowingCompanyBanner && stickyBarFixed)}
      ref={mergeRefs([ref, stickyBarRef])}
    >
      <Styled.EditBarSticky
        $isSticky={!!stickyBarFixed}
        $isEditing={isEditing}
        $isShowingCompanyBanner={
          !!(pageContext.isShowingCompanyBanner && !stickyBarFixed)
        }
        style={
          typeof entry !== 'undefined' && entry.contentRect.width > 0
            ? { width: entry.contentRect?.width }
            : { visibility: 'hidden' }
        }
      >
        {displayBackButton ? (
          <EditBarBack />
        ) : (
          <Styled.InfoContainer>
            <IconInfo height={24} width={24} />
            <Styled.Info size="small">
              <FormattedMessage
                id={
                  isEditing
                    ? 'EP_EDIT_INFO_EDIT_VIEW'
                    : 'EP_EDIT_INFO_VISITOR_VIEW'
                }
              />
            </Styled.Info>
          </Styled.InfoContainer>
        )}
        <Styled.ActionContainer>
          {isEditing ? (
            <>
              {pageContext.isCompanyPage ? (
                <>
                  <Styled.Button
                    icon={IconInfo}
                    size="small"
                    variant="secondary"
                    data-testid="EP_EDIT_PAGE_INFO_BUTTON"
                    onClick={() => {
                      trackOpenNavInfoBox();
                      pageInfoModal.handleShow();
                    }}
                    aria-label="Info"
                  />

                  <ModalPage
                    isShown={pageInfoModal.show}
                    closeButton={{ onClick: () => pageInfoModal.handleHide() }}
                    isAnimated
                    noGrid
                  >
                    <Styled.ModalPageInfoWrapper>
                      <PageInfo />
                    </Styled.ModalPageInfoWrapper>
                  </ModalPage>
                  <Styled.Button
                    icon={IconStatistics}
                    size="small"
                    variant="secondary"
                    href={getTrackingUrl({
                      to: XBM_STATISTICS_URL(pageContext.pageSlug),
                      trackingKey: 'viewXBM',
                    })}
                    target="_blank"
                    aria-label="Statistics"
                    data-testid="EP_EDIT_PAGE_STATISTICS_BUTTON"
                  />
                  <Styled.Button
                    variant="secondary"
                    size="small"
                    href={PROMOTE_URL(pageContext.globalId)}
                    onClick={(ev) => {
                      ev.preventDefault();
                      window.open(
                        getTrackingUrl({
                          to: PROMOTE_URL(pageContext.globalId),
                          trackingKey: 'viewPagePromote',
                        })
                      );
                    }}
                  >
                    <FormattedMessage id="EP_EDIT_ACTION_PROMOTE" />
                  </Styled.Button>
                </>
              ) : null}
            </>
          ) : (
            <>
              {displayNewsButtons ? (
                <CreatePostButton
                  page={
                    isSubpage
                      ? 'entity_pages/subpage/news'
                      : 'entity_pages/overview'
                  }
                >
                  {(onClick) => (
                    <Styled.CreatePostButton
                      variant="tertiary"
                      size="small"
                      onClick={() => onClick()}
                      data-testid="EP_EDIT_PAGE_COMMBOX_BUTTON"
                    >
                      <FormattedMessage id="EP_EDIT_ACTION_POST" />
                    </Styled.CreatePostButton>
                  )}
                </CreatePostButton>
              ) : null}
            </>
          )}
          <ToggleEditMode />
        </Styled.ActionContainer>
      </Styled.EditBarSticky>
    </Styled.EditBar>
  );
};
